import { STATUS } from '../../constants';

const { APPLICATION } = STATUS;

const { PENDING, ARCHIVED, HIRED, HIRING, ONGOING, REJECTED } = APPLICATION;

/**
 *
 * @param {string} status
 * @returns {string}
 */
export const getLabelType = status => {
  switch (status) {
    case HIRED:
      return 'complete';
    case HIRING:
      return 'success';
    case ONGOING:
      return 'warning';
    case REJECTED:
    case ARCHIVED:
      return 'error';
    default:
      return 'default';
  }
};

/**
 *
 * @param {string} status
 * @returns {string}
 */
export const getBadgeColor = status => {
  switch (status) {
    case HIRED:
      return 'main';
    case HIRING:
      return 'blue';
    case ONGOING:
      return 'secondary';
    case REJECTED:
    case ARCHIVED:
      return 'red';
    case PENDING:
      return 'blue';
    default:
      return 'white';
  }
};

const getSkillsObject = skills =>
  skills.reduce((skills, skill) => {
    skills[skill.id] = skill;
    return skills;
  }, {});

const highlightSkill = skill => ({ ...skill, highlighted: true });

export const sortMatchingSkills = (missionSkills, profileSkills) => {
  const missionSkillsObject = getSkillsObject(missionSkills);

  const { common, unrelated } = profileSkills.reduce(
    (skills, skill) => {
      const isSkillInMission = !!missionSkillsObject[skill.id];
      skills[isSkillInMission ? 'common' : 'unrelated'].push(skill);
      return skills;
    },
    { common: [], unrelated: [] }
  );

  return common.concat(unrelated);
};

export const highlightMatchingSkills = (missionSkills, profileSkills) => {
  const missionSkillsObject = getSkillsObject(missionSkills);
  return profileSkills.map(skill => (missionSkillsObject[skill.id] ? highlightSkill(skill) : skill));
};

export const sortAndFlagMatchingSkills = (missionSkills, profileSkills) => {
  const profileSkillsObject = getSkillsObject(profileSkills);
  const mandatorySkills = missionSkills.filter(skill => skill?.isMandatory);

  const skillsToHighlight = [...mandatorySkills, ...profileSkills].filter(
    (value, index, self) => index === self.findIndex(t => t.id === value.id)
  );

  const { common, unrelated } = skillsToHighlight.reduce(
    (skills, skill) => {
      if (skill?.isMandatory) {
        const profileSkill = profileSkillsObject[skill.id];
        skill.isMandatorySkillMissing = !profileSkill;
        if (profileSkill) {
          skill.level = profileSkill.level;
        }
      }
      if (skill?.isMandatory) skills.common.push(skill);
      else skills.unrelated.push(skill);
      return skills;
    },
    { common: [], unrelated: [] }
  );

  return common.concat(unrelated);
};

const initialApplicationFilterValues = Object.values(APPLICATION);

/**
 *
 * @returns {Object}
 */
export const getApplicationFiltersInLocalStorage = () => {
  const filters = localStorage.getItem('applicationFilters');
  return filters ? JSON.parse(filters) : initialApplicationFilterValues;
};

/**
 *
 * @param {Object} filters
 * @returns {void}
 */
export const setApplicationFiltersInLocalStorage = filters => {
  localStorage.setItem('applicationFilters', JSON.stringify(filters));
};

const orderStatuses = statuses => {
  const orderedKeys = [PENDING, ONGOING, HIRING, HIRED, REJECTED, ARCHIVED];
  return orderedKeys.reduce((orderedStatus, key) => {
    if (statuses[key]) {
      orderedStatus[key] = statuses[key];
    }
    return orderedStatus;
  }, {});
};

/**
 *
 * @param {Object} applications
 * @returns {Object}
 */
export const extractStatus = applications => {
  const statuses = Object.values(applications).reduce((statuses, mission) => {
    mission.applications.map(({ status }) => {
      statuses[status] = statuses[status] + 1 || 1;
      return statuses;
    });
    return statuses;
  }, {});

  return orderStatuses(statuses);
};

/**
 *
 * @param {Object} applications
 * @param {Array} applicationFilters
 * @returns {Array}
 */
export const getVisibleMissionApplications = (applications, applicationFilters) =>
  Object.entries(applications).map(([missionId, mission]) => ({
    id: missionId,
    title: mission.title,
    applications: (mission.applications || []).filter(application => applicationFilters.includes(application.status))
  }));

/**
 *
 * @param {Object} applications
 * @returns {number}
 */
export const getNumberOfApplications = applications =>
  Object.values(applications).reduce((acc, mission) => acc + mission?.applications?.length ?? 0, 0);
